import type Intercom from "@intercom/messenger-js-sdk";
import { AbstractTrackingService } from "./abstract-tracking-service";
import type { BrandTheme, Me } from "@/generated/graphql";
import type { TrackEventEnum, TrackOptions } from "./tracking-types";
import type { ChannelMetadata } from "@/models";

export const intercomLauncherId = "intercom-launcher";

type IntercomSettings = Parameters<typeof Intercom>[0];

export class IntercomService extends AbstractTrackingService {
  name = "IntercomService";

  private isInitialized = false;
  private app_id: string;
  private currentTheme: BrandTheme | null = null;

  private settings: IntercomSettings | null = null;

  constructor(
    app_id: string,
    intercom?: typeof Intercom,
    settings?: IntercomSettings,
  ) {
    super();
    this.app_id = app_id;
    this.settings = settings ?? null;
  }

  public getSettings(update?: IntercomSettings): IntercomSettings {
    const latest = {
      app_id: this.app_id,
      ...this.settings,
      ...update,
    };

    this.settings = latest;

    return latest;
  }

  public async init(channelMetadata: ChannelMetadata) {
    await super.init(channelMetadata);

    const { default: intercomFunction } =
      await import("@intercom/messenger-js-sdk");
    console.log(
      `[${this.constructor.name}] Initializing Intercom with app_id: ${this.app_id}`,
    );

    // Boot Intercom with minimal settings to hide the default launcher immediately
    // This prevents the default launcher from appearing before user identification
    intercomFunction(
      this.getSettings({
        app_id: this.app_id,
        hide_default_launcher: true,
      }),
    );

    this.isInitialized = true;

    console.log(
      `[${this.constructor.name}] Intercom booted with hidden default launcher`,
    );
  }

  public identifyChannel(): void {
    // Do nothing
  }

  public async identifyUser(user: Me): Promise<void> {
    console.log(`[${this.constructor.name}] Identifying user: ${user.id}`);
    if (!this.isInitialized) {
      console.error(
        `[${this.constructor.name}] Intercom is not initialized, unable to identify user`,
      );
      return;
    }

    const { update } = await import("@intercom/messenger-js-sdk");

    try {
      update(
        this.getSettings({
          app_id: this.app_id,
          user_id: user.id,
          name: user.username,
          email: user.email,
          created_at: Math.floor(new Date(user.created_at).getTime() / 1000),
          action_color: this.currentTheme?.brand_primary,
          hide_default_launcher: true,
          background_color: this.currentTheme?.brand_surface_hover,
        }),
      );
      this.initialized = true;
    } catch (error) {
      console.error(
        `[${this.constructor.name}] Unable to identify user,`,
        error,
      );
    }
  }

  public async updateTheme(theme: BrandTheme): Promise<void> {
    if (!this.isInitialized) {
      console.error(
        `[${this.constructor.name}] Intercom is not initialized, unable to update theme`,
      );
      return;
    }

    const { update } = await import("@intercom/messenger-js-sdk");

    update(
      this.getSettings({
        app_id: this.app_id,
        action_color: theme.brand_primary,
        background_color: theme.brand_surface_hover,
      }),
    );
  }

  public async trigger() {
    if (!this.isInitialized) {
      console.error(
        `[${this.constructor.name}] Intercom is not initialized, unable to trigger`,
      );
      return;
    }

    const { show } = await import("@intercom/messenger-js-sdk");
    show();
  }

  public async resetIdentity() {
    if (!this.isInitialized) {
      console.error(
        `[${this.constructor.name}] Intercom is not initialized, unable to reset identity`,
      );
      return;
    }

    const { shutdown, default: intercomFunction } =
      await import("@intercom/messenger-js-sdk");

    console.log(`[${this.constructor.name}] Resetting identity`);
    try {
      shutdown();
      // After shutdown, re-boot with anonymous settings to keep default launcher hidden
      // This ensures the default launcher stays hidden even for non-authenticated users
      intercomFunction(
        this.getSettings({
          app_id: this.app_id,
          hide_default_launcher: true,
        }),
      );
      console.log(
        `[${this.constructor.name}] Session reset and Intercom re-booted anonymously`,
      );
    } catch (error) {
      console.error(
        `[${this.constructor.name}] Error resetting session:`,
        error,
      );
    }
  }

  public track<T extends TrackEventEnum>(_options: TrackOptions<T>) {}
}
